import {FormHelperText, Grid} from '@mui/material';
import {FormikErrors} from 'formik';
import {FAKE_VALUE_EMPTY, FormProps, OptionValue} from "../../model/form";
import {JsonArticleSexEnum, JsonArticleType, JsonArticleTypeInfo, JsonEventInfo} from "../../generated-api";
import {useAppTranslation} from "../../services/i18n";
import FormContainer from "../form/FormContainer";
import {TextFormField} from "../form/TextFormField";
import {SimpleSelectFormField} from "../form/SimpleSelectFormField";
import {CheckboxField} from "../form/CheckboxField";
import {useAppDispatch} from "../../store";
import {ArticleCommonSize} from "../../helpers/format";
import {useCallback, useEffect, useMemo, useState} from "react";
import {fetchArticleTypeOptions} from "../../store/articleTypes";
import {fetchEvents} from "../../store/events";
import {getApiResult} from "../../helpers/api";

interface Props extends FormProps<JsonArticleType> {
}

interface FormValues {
    articleTypeId?: number;
    eventId?: number;
    title?: string;
    sourceArticleTypeId?: number;
    isOffered?: boolean;
    reuseTypeIds?: number[];
    isExtra?: boolean;
    sizesU?: string;
    sizesM?: string;
    sizesF?: string;
}

const parseSizes = (value?: string): string[] => {
    const sizes: string[] = [];
    (value || '').split(',').forEach((s) => {
        const size = s.trim();
        if (size && sizes.indexOf(size) < 0) {
            sizes.push(size);
        }
    });
    return sizes;
};

const formatSizes = (sizes?: string[]): string => (sizes || []).join(', ');

/** Clothing is reused a few seasons back, older events are not worth offering as a source. */
const PREVIOUS_EVENTS = 3;

const isCommonSize = (size: string): boolean => Object.keys(ArticleCommonSize).indexOf(size) >= 0;

/** A product of a previous event, either its season one or a plain type of the years before that. */
interface SourceType {
    at: JsonArticleTypeInfo;
    event: JsonEventInfo;
    year?: string;
}

const EventArticleTypeForm = (props: Props) => {

    const t = useAppTranslation();
    const dispatch = useAppDispatch();
    const {item, onSave} = props;
    const isNew = !item.articleTypeId;
    const eventId = item.eventId;

    const [sources, setSources] = useState<SourceType[]>();
    const [year, setYear] = useState<string>();

    // both this season's products and the leftovers of the past ones continue a type of a previous event,
    // an existing one only shows what it continues
    useEffect(() => {
        if (!eventId) {
            return;
        }
        let cancelled = false;
        (async () => {
            const events = getApiResult<JsonEventInfo[]>(await dispatch(fetchEvents({orderCol: 'eventId', orderDir: 'desc'}))) || [];
            const previous = events.filter((e) => !!e.eventId && !!eventId && e.eventId < eventId).slice(0, PREVIOUS_EVENTS);
            const types = await Promise.all(previous.map((e) => dispatch(fetchArticleTypeOptions({eventId: e.eventId}))));
            if (cancelled) {
                return;
            }
            const found: SourceType[] = [];
            previous.forEach((event, i) => (getApiResult<JsonArticleTypeInfo[]>(types[i]) || [])
                .forEach((at) => found.push({at, event, year: event.eventFrom?.substring(0, 4)})));
            setYear(events.find((e) => e.eventId === eventId)?.eventFrom?.substring(0, 4));
            setSources(found);
        })();
        return () => {
            cancelled = true;
        };
    }, [eventId, dispatch]);

    const values = useMemo((): FormValues => ({
        articleTypeId: item.articleTypeId,
        eventId: item.eventId,
        title: item.title,
        sourceArticleTypeId: FAKE_VALUE_EMPTY,
        isOffered: item.isOffered !== false,
        reuseTypeIds: item.reuseTypeIds,
        isExtra: item.variants?.isExtra,
        sizesU: formatSizes(item.variants?.sizes?.[JsonArticleSexEnum.U]),
        sizesM: formatSizes(item.variants?.sizes?.[JsonArticleSexEnum.M]),
        sizesF: formatSizes(item.variants?.sizes?.[JsonArticleSexEnum.F]),
    }), [item]);

    const sourceOptions = useMemo((): OptionValue[] => {
        const options: OptionValue[] = [{value: FAKE_VALUE_EMPTY, label: t('(bez návaznosti)')}];
        (sources || []).forEach((s) => options.push({
            value: s.at.articleTypeId!,
            label: (s.at.title || '') + ' – ' + (s.event.abbr || s.event.title || ''),
        }));
        return options;
    }, [sources, t]);

    /**
     * The chain is built when the type is created and stays as it is - only its newest link is worth
     * showing, the seasons behind it are what that one in turn continues.
     */
    const reuseTitle = useMemo((): string | undefined => {
        const reuseTypeIds = item.reuseTypeIds;
        if (!reuseTypeIds?.length) {
            return undefined;
        }
        // the latest season, and there the type the invite asked about rather than one of its products
        const newest = (sources || [])
            .filter((s) => reuseTypeIds.some((id) => id === s.at.articleTypeId))
            .sort((a, b) => (b.event.eventId || 0) - (a.event.eventId || 0)
                || Number(b.at.isOffered !== false) - Number(a.at.isOffered !== false)
                || (a.at.articleTypeId || 0) - (b.at.articleTypeId || 0))[0];
        return newest
            ? (newest.at.title || '') + ' – ' + (newest.event.abbr || newest.event.title || '')
            : undefined;
    }, [item.reuseTypeIds, sources]);

    const handleValidate = useCallback((v: FormValues) => {
        const errors = {} as FormikErrors<FormValues>;

        if (!v.title) {
            errors.title = t('Zadejte prosím název');
        }

        const all = [
            {name: 'sizesU' as const, sizes: parseSizes(v.sizesU)},
            {name: 'sizesM' as const, sizes: parseSizes(v.sizesM)},
            {name: 'sizesF' as const, sizes: parseSizes(v.sizesF)},
        ];
        if (!all.find((s) => s.sizes.length > 0)) {
            errors.sizesU = t('Zadejte prosím alespoň jednu velikost');
        }
        if (!v.isExtra) {
            // sizes outside the common list would have no column in the stock grid
            all.forEach((s) => {
                const unknown = s.sizes.filter((size) => !isCommonSize(size));
                if (unknown.length) {
                    errors[s.name] = t('Nestandardní velikosti ({{sizes}}) vyžadují vlastní řadu', {sizes: unknown.join(', ')});
                }
            });
        }
        return errors;
    }, [t]);

    const handleSave = useCallback((v: FormValues) => {
        const sizes: { [key: string]: string[] } = {};
        const u = parseSizes(v.sizesU);
        const m = parseSizes(v.sizesM);
        const f = parseSizes(v.sizesF);
        if (u.length) {
            sizes[JsonArticleSexEnum.U] = u;
        }
        if (m.length) {
            sizes[JsonArticleSexEnum.M] = m;
        }
        if (f.length) {
            sizes[JsonArticleSexEnum.F] = f;
        }

        const json: JsonArticleType = {
            articleTypeId: v.articleTypeId,
            eventId: v.eventId,
            title: v.title,
            // the server builds the chain from the source: the whole family, or that one older article
            sourceArticleTypeId: v.sourceArticleTypeId === FAKE_VALUE_EMPTY ? undefined : v.sourceArticleTypeId,
            isOffered: v.isOffered,
            reuseTypeIds: v.reuseTypeIds,
            variants: {sizes, isExtra: v.isExtra || undefined},
        };

        return onSave ? onSave(json) : undefined;
    }, [onSave]);

    const formProps = {...props, item: values} as FormProps<FormValues>;

    return (
        <FormContainer {...formProps} validate={handleValidate} onSave={handleSave} children={(formikProps) => {
            // a product is always offered in the sizes of the type it continues
            const prefillSizes = (from?: JsonArticleTypeInfo) => {
                if (!from || !from.variants?.sizes) {
                    return;
                }
                const v = formikProps.values;
                if (v.sizesU || v.sizesM || v.sizesF) {
                    return; // do not overwrite what is already filled in
                }
                formikProps.setFieldValue('sizesU', formatSizes(from.variants.sizes[JsonArticleSexEnum.U]));
                formikProps.setFieldValue('sizesM', formatSizes(from.variants.sizes[JsonArticleSexEnum.M]));
                formikProps.setFieldValue('sizesF', formatSizes(from.variants.sizes[JsonArticleSexEnum.F]));
                formikProps.setFieldValue('isExtra', from.variants.isExtra);
            };
            // this season's product is named by the year, a leftover keeps the name it had
            const suggestTitle = (source: SourceType, isOffered: boolean) => (source.at.title || '') + (isOffered
                ? (year ? ' ' + year : '')
                : (source.year && (source.at.title || '').indexOf(source.year) < 0 ? ' ' + source.year : ''));

            const findSource = (sourceArticleTypeId: any) => (sources || [])
                .find((s) => s.at.articleTypeId === sourceArticleTypeId);

            const handleSourceChange = (sourceArticleTypeId: any) => {
                const source = findSource(sourceArticleTypeId);
                if (!source) {
                    return;
                }
                const isOffered = formikProps.values.isOffered !== false;
                formikProps.setFieldValue('isOffered', isOffered);
                if (!formikProps.values.title) {
                    formikProps.setFieldValue('title', suggestTitle(source, isOffered));
                }
                prefillSizes(source.at);
            };

            const handleOfferedChange = (isOffered: any) => {
                const source = findSource(formikProps.values.sourceArticleTypeId);
                // keep the suggested name in sync, a name typed by hand is left alone
                if (source && (!formikProps.values.title || formikProps.values.title === suggestTitle(source, !isOffered))) {
                    formikProps.setFieldValue('title', suggestTitle(source, !!isOffered));
                }
            };

            return <>
                {isNew && !!sources?.length && <>
                    <Grid item xs={12}>
                        <SimpleSelectFormField name="sourceArticleTypeId" label={t('Navazuje na artikl z minulých let')}
                            options={sourceOptions}
                            onChange={handleSourceChange}/>
                    </Grid>
                </>}
                <Grid item xs={12}>
                    <CheckboxField name="isOffered" label={t('Letošní produkt (ptát se v pozvánce)')} onChange={handleOfferedChange}/>
                </Grid>
                <Grid item xs={12}>
                    <FormHelperText>{t('Letošní produkt (např. Bunda 2027) se v pozvánce nabízí bez ročníku, tedy jako Bunda, '
                        + 'a navazuje na celou rodinu z minulých let. Bez zaškrtnutí jde o zbytek staršího ročníku '
                        + '(např. Mikina 2026): v pozvánce se nenabízí, ale jde ho vydat a vést mu sklad.')}</FormHelperText>
                </Grid>
                <Grid item xs={12}>
                    <TextFormField name="title" label={t('Název typu')} type={'text'} maxlength={100}/>
                </Grid>
                <Grid item xs={12}>
                    <TextFormField name="sizesU" label={t('Velikosti unisex')} type={'text'} maxlength={500}/>
                </Grid>
                <Grid item xs={12}>
                    <TextFormField name="sizesM" label={t('Velikosti muži')} type={'text'} maxlength={500}/>
                </Grid>
                <Grid item xs={12}>
                    <TextFormField name="sizesF" label={t('Velikosti ženy')} type={'text'} maxlength={500}/>
                </Grid>
                <Grid item xs={12}>
                    <FormHelperText>{t('Velikosti oddělte čárkou, prázdné pole = daná varianta se nenabízí. Běžné velikosti: {{sizes}}',
                        {sizes: Object.keys(ArticleCommonSize).join(', ')})}</FormHelperText>
                </Grid>
                <Grid item xs={12}>
                    <CheckboxField name="isExtra" label={t('Vlastní řada velikostí (např. čísla bot) – zadává se ve skladu samostatně')}/>
                </Grid>
                {!isNew && !!reuseTitle && <Grid item xs={12}>
                    <FormHelperText>{t('Navazuje na: {{title}}', {title: reuseTitle})}</FormHelperText>
                </Grid>}
            </>;
        }}/>
    );
}

export default EventArticleTypeForm;
